export interface AudioRecorderConfig {
  maxRecordingTime?: number;
  stopDelaySeconds?: number; // Delay in seconds before actually stopping recording
  onDataAvailable?: (chunk: Blob) => void;
  onRecordingStart?: () => void;
  onRecordingStop?: (audioBlob: Blob) => void;
  onRecordingCancel?: () => void;
  onTimeUpdate?: (time: number) => void;
  onAutoStop?: () => void;
  onError?: (error: unknown) => void;
  onProcessingStart?: () => void; // New callback for immediate processing UI
}

export class AudioRecorder {
  private mediaRecorder: MediaRecorder | null = null;
  private audioChunks: Blob[] = [];
  private timer: number | null = null;
  private recordingTime = 0;
  private config: AudioRecorderConfig;
  private isRecording = false;

  constructor(config: AudioRecorderConfig = {}) {
    this.config = {
      maxRecordingTime: 10,
      stopDelaySeconds: 0.5,
      ...config,
    };
  }

  async startRecording(): Promise<void> {
    try {
      this.recordingTime = 0;
      this.audioChunks = [];

      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });

      // Try different MIME types for browser compatibility
      const mimeTypes = [
        "audio/webm;codecs=opus",
        "audio/webm",
        "audio/mp4",
        "audio/ogg;codecs=opus",
        "audio/ogg",
      ];

      let selectedMimeType = "";
      for (const mimeType of mimeTypes) {
        if (MediaRecorder.isTypeSupported(mimeType)) {
          selectedMimeType = mimeType;
          break;
        }
      }

      const mediaRecorder = selectedMimeType
        ? new MediaRecorder(stream, { mimeType: selectedMimeType })
        : new MediaRecorder(stream);

      this.mediaRecorder = mediaRecorder;

      // Handle data collection
      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          this.audioChunks.push(e.data);
          this.config.onDataAvailable?.(e.data);
        }
      };

      // Handle recording stop
      mediaRecorder.onstop = () => {
        this.cleanup();
        
        if (this.audioChunks.length > 0) {
          const audioBlob = new Blob(this.audioChunks, {
            type: mediaRecorder.mimeType,
          });
          this.config.onRecordingStop?.(audioBlob);
        }
      };

      // Start recording and timer
      mediaRecorder.start(1000); // Collect data every second
      this.isRecording = true;
      this.startTimer();
      
      this.config.onRecordingStart?.();
    } catch {
      this.handleError("Could not access microphone. Please check permissions.");
    }
  }

  stopRecording(): void {
    if (!this.mediaRecorder || !this.isRecording) return;

    // Immediately show processing UI
    this.config.onProcessingStart?.();
    
    // Set recording state to false to update UI
    this.isRecording = false;
    this.stopTimer();

    // Continue recording for configured delay to capture final audio
    setTimeout(() => {
      if (this.mediaRecorder) {
        this.mediaRecorder.stop();
      }
    }, (this.config.stopDelaySeconds || 0.5) * 1000);
  }

  cancelRecording(): void {
    if (!this.mediaRecorder || !this.isRecording) return;

    this.isRecording = false;
    this.stopTimer();
    this.audioChunks = [];
    
    // Stop recording without triggering onRecordingStop
    this.mediaRecorder.onstop = () => {
      this.cleanup();
      this.config.onRecordingCancel?.();
    };
    
    this.mediaRecorder.stop();
  }

  getIsRecording(): boolean {
    return this.isRecording;
  }

  getRecordingTime(): number {
    return this.recordingTime;
  }

  private startTimer(): void {
    this.timer = window.setInterval(() => {
      this.recordingTime += 1;
      this.config.onTimeUpdate?.(this.recordingTime);

      if (this.config.maxRecordingTime && this.recordingTime >= this.config.maxRecordingTime) {
        this.config.onAutoStop?.();
        this.stopRecording();
      }
    }, 1000);
  }

  private stopTimer(): void {
    if (this.timer) {
      clearInterval(this.timer);
      this.timer = null;
    }
  }

  private cleanup(): void {
    if (this.mediaRecorder?.stream) {
      this.mediaRecorder.stream
        .getTracks()
        .forEach((track) => track.stop());
    }
  }

  private handleError(error: unknown): void {
    console.error("Audio recording error:", error);
    this.isRecording = false;
    this.stopTimer();
    this.config.onError?.(error);
  }

  // Clean up resources
  destroy(): void {
    this.stopTimer();
    this.cleanup();
    this.mediaRecorder = null;
    this.audioChunks = [];
  }
}

// Convenience function for simple use cases
export const createAudioRecorder = (config: AudioRecorderConfig = {}) => {
  return new AudioRecorder(config);
};